import {eAdmCacheBustTokens, type ICacheBustTokens} from "../util/assetCacheBust";
import {withCacheBust} from "../util/cacheBustQuery";

let ehubAssetCacheBustPromise: Promise<ICacheBustTokens>;

/**
 * Loads eHub build.json once (no-store) and returns `ver` + `t` cache-bust tokens.
 * Prefers build.json `version` / `timestamp`; falls back to eAdm BUILD_VERSION / BUILD_DATE.
 */
function getEHubAssetCacheBust(): Promise<ICacheBustTokens> {
    if (!ehubAssetCacheBustPromise) {
        ehubAssetCacheBustPromise = (async () => {
            try {
                const res = await fetch('ehub/build.json', {cache: 'no-store'});
                if (!res.ok) {
                    throw new Error(String(res.status));
                }
                const data = await res.json() as {version?: number | string; timestamp?: number | string};
                const ver = data.version ?? data.timestamp;
                const t = data.timestamp ?? data.version;
                if (ver == null || t == null) {
                    throw new Error('missing version');
                }
                return {ver: String(ver), t: String(t)};
            } catch {
                return eAdmCacheBustTokens();
            }
        })();
    }
    return ehubAssetCacheBustPromise;
}

/** Builds a cache-busted URL for an eHub static asset (`?ver=&t=`). */
export async function getEHubAssetUrl(fileName: 'index.js' | 'index.css'): Promise<string> {
    const {ver, t} = await getEHubAssetCacheBust();
    return withCacheBust(`ehub/${fileName}`, ver, t);
}

/** Points every eHub stylesheet link at the current build.json ver/t. */
export async function applyEHubStylesheetCacheBust(): Promise<void> {
    const href = await getEHubAssetUrl('index.css');
    document
        .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href*="ehub/index.css"]')
        .forEach(link => {
            link.href = href;
        });
}
